/**
 * Painel de entrega da credencial — aparece DEPOIS de criar um usuário ou
 * redefinir a senha, no lugar de fechar o modal.
 *
 * A senha é mostrada UMA vez e não é recuperável depois (o servidor só guarda o
 * hash). Por isso a tela não fecha sozinha: fechar antes de copiar significa
 * redefinir de novo.
 *
 * Serve os dois shells; cada um só a envolve no seu container, como o
 * `SeletorCicloGlobal` já faz.
 */
import { PAPEIS } from "@caint/data";
import { Icon } from "@caint/design-system";
import { mensagemAcesso } from "@caint/shared";
import { useMemo, useState } from "react";

export interface CredencialProntaProps {
  /** `true` enquanto a senha provisória está sendo buscada no servidor. */
  carregandoSenha?: boolean;
  nome: string;
  /** Login = e-mail. */
  matricula: string;
  /** Ausente = reenvio das instruções (a senha original não é recuperável). */
  senha?: string;
  papel: string;
  /** Nome da unidade vinculada (só para contas `unidade`). */
  unidadeNome?: string;
  /** `true` quando é redefinição de senha, não primeiro acesso. */
  redefinicao?: boolean;
}

/** Rótulo legível do perfil. Resolvido aqui porque `shared` não pode importar `data`. */
function rotuloPerfil(papel: string): string {
  return PAPEIS.find((p) => p.key === papel)?.nome ?? papel;
}

export function CredencialPronta({
  nome,
  matricula,
  senha,
  papel,
  unidadeNome,
  redefinicao,
  carregandoSenha,
}: CredencialProntaProps) {
  const [copiado, setCopiado] = useState(false);
  const [falhou, setFalhou] = useState(false);

  const texto = useMemo(
    () =>
      mensagemAcesso({
        nome,
        matricula,
        senha,
        papel,
        perfil: rotuloPerfil(papel),
        unidadeNome,
        // A origem real do app — o mesmo caminho usado no link do formulário público.
        origem: typeof window === "undefined" ? "" : window.location.origin,
        redefinicao,
      }),
    [nome, matricula, senha, papel, unidadeNome, redefinicao],
  );

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setFalhou(false);
      window.setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Sem permissão de clipboard (http, navegador antigo): o texto segue
      // visível e selecionável abaixo — não deixa o gestor sem saída.
      setFalhou(true);
    }
  };

  if (carregandoSenha) {
    return (
      <div className="cred">
        <p className="spec">Buscando a senha…</p>
      </div>
    );
  }

  return (
    <div className="cred">
      <div className="cred-aviso">
        <span aria-hidden="true">🔐</span>
        <div>
          <b>Anote ou copie agora.</b> A senha aparece uma única vez — depois de fechar, só redefinindo outra.
        </div>
      </div>

      <div className="cred-campos">
        <div>
          <span className="spec">Usuário</span>
          <div className="cred-valor">{matricula}</div>
        </div>
        {senha && (
          <div>
            <span className="spec">Senha</span>
            <div className="cred-valor cred-senha">{senha}</div>
          </div>
        )}
      </div>

      <button type="button" className="m-btn primary cred-copiar" onClick={() => void copiar()}>
        <Icon name={copiado ? "check" : "doc"} size={16} />
        {copiado ? "Mensagem copiada!" : "Copiar mensagem para WhatsApp"}
      </button>

      {falhou && (
        <p className="spec" style={{ color: "var(--danger)" }}>
          Não consegui copiar automaticamente. Selecione o texto abaixo e copie à mão.
        </p>
      )}

      <details className="cred-previa">
        <summary className="spec">Ver a mensagem</summary>
        <pre className="cred-texto">{texto}</pre>
      </details>
    </div>
  );
}
